import React, { useState, useEffect } from 'react';
import { 
  User, 
  Bell, 
  Mail, 
  Lock, 
  CreditCard, 
  Shield, 
  LogOut, 
  CheckCircle, 
  AlertCircle,
  Camera,
  Save,
  X
} from 'lucide-react';
import { supabase } from '../supabase/client';
import { Button } from '@mantine/core';
import { Link } from 'react-router-dom';
import OrderHistory from '../components/OrderHistory';
import { useQuery } from '@tanstack/react-query';
import { Database } from '@/database.types';

const communicationPreferences = [
  { value: 0, label: 'Email' },
  { value: 1, label: 'In-app notifications only' },
  { value: 2, label: 'None' },
];

type Profile = Database["public"]["Tables"]["profiles"]["Update"];

const AccountSettingsPage = () => {
  // State for user profile data
  const [profile, setProfile] = useState<Profile>({
    full_name: '',
    email: '',
    avatar_url: null,
    settings_anonymous_research: true,
    settings_personalized_recommendations: true,
    communication_preference: 0,
    notifications: null,
  });
  
  // State for password change
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  
  // State for notification settings
  const [notifications, setNotifications] = useState({
    newTests: true,
    reportsAnalytics: true,
    marketingEmails: false,
  });
  
  // State for subscription details
  const [subscription, setSubscription] = useState({
    plan: 'Premium',
    status: 'Active',
    nextBillingDate: '2025-04-02',
    cardLast4: '4242',
  });
  
  // State for active section
  const [activeSection, setActiveSection] = useState('profile');
  
  // State for alert messages
  const [alert, setAlert] = useState({
    show: false,
    type: '',
    message: '',
  });
  
  // State for loading
  const [loading, setLoading] = useState(false);

  async function fetchUserProfile() {
    setLoading(true);
    
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      if (user) {
        // Fetch profile data from profiles table
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
          
        if (error) throw error;
        
        // Set profile state with fetched data
        return data;
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      showAlert('error', 'Failed to load your profile. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const { data:_profile } = useQuery({
    queryKey: ['Profile', { page: 'settings '}],
    queryFn: async() => {
      const p = await fetchUserProfile();

      setNotifications({
        ...notifications,
        ...(p?.notifications && typeof p?.notifications === 'object' && (p?.notifications ?? {})),
      });

      p && setProfile(p);

      return p;
    }
  });

  // Show alert message
  const showAlert = (type: string, message: string) => {
    setAlert({
      show: true,
      type,
      message,
    });
    
    // Hide alert after 5 seconds
    setTimeout(() => {
      setAlert({ show: false, type: '', message: '' });
    }, 5000);
  };
  
  // Handle profile form input change
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile({
      ...profile,
      [name]: value,
    });
  };
  
  // Handle password form input change
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswords({
      ...passwords,
      [name]: value,
    });
  };
  
  // Handle notification toggle change
  const handleNotificationChange = (settingName: string) => {
    setNotifications({
      ...notifications,
      [settingName]: !notifications[settingName],
    });
  };
  
  // Update user profile
  const updateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Update profile data
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: profile.full_name,
          // updated_at: new Date(),
        })
        .eq('id', user?.id ?? '');
        
      if (error) throw error;
      
      showAlert('success', 'Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      showAlert('error', 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Change password
  const changePassword = async (e: any) => {
    e.preventDefault();
    
    // Validate passwords
    if (passwords.newPassword !== passwords.confirmPassword) {
      showAlert('error', 'New passwords do not match.');
      return;
    }
    
    if (passwords.newPassword.length < 6) {
      showAlert('error', 'Password must be at least 6 characters long.');
      return;
    }
    
    setLoading(true);
    
    try {
      // Update password in Supabase Auth
      const { error } = await supabase.auth.updateUser({
        password: passwords.newPassword,
      });
      
      if (error) throw error;
      
      // Clear password fields
      setPasswords({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      
      showAlert('success', 'Password changed successfully!');
    } catch (error) {
      console.error('Error changing password:', error);
      showAlert('error', 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Save notification settings
  const saveNotificationSettings = async () => {
    setLoading(true);
    
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Update notification settings
      const { error } = await supabase
        .from('profiles')
        .update({
          settings_anonymous_research: profile.settings_anonymous_research,
          settings_personalized_recommendations: profile.settings_personalized_recommendations,
          communication_preference: profile.communication_preference, 
          notifications: profile.notifications,
        })
        .eq('id', user?.id ?? '');
        
      if (error) throw error;
      
      showAlert('success', 'Notification settings saved!');
    } catch (error) {
      console.error('Error saving notification settings:', error);
      showAlert('error', 'Failed to save notification settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle avatar upload
  const uploadAvatar = async (e: any) => {
    const file = e.target.files[0];
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random()}.${fileExt}`;
    const filePath = `avatars/${fileName}`;
    
    setLoading(true);
    
    try {
      // Upload file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);
        
      if (uploadError) throw uploadError;
      
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);
        
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Update avatar URL in profiles
      const { error } = await supabase
        .from('profiles')
        .update({
          avatar_url: publicUrl,
        })
        .eq('id', user?.id ?? '');
        
      if (error) throw error;
      
      // Update state
      setProfile({
        ...profile,
        avatar_url: publicUrl,
      });
      
      showAlert('success', 'Avatar updated successfully!');
    } catch (error) {
      console.error('Error uploading avatar:', error);
      showAlert('error', 'Failed to upload avatar. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Sign out
  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) throw error;
      
      // Redirect to login page or home page
      window.location.href = '/login';
    } catch (error) {
      console.error('Error signing out:', error);
      showAlert('error', 'Failed to sign out. Please try again.');
    }
  };
  
  // Render navigation item
  const NavItem = ({ icon, label, id }) => (
    <button
      onClick={() => setActiveSection(id)}
      className={`flex items-center w-full p-3 rounded-lg transition-colors ${
        activeSection === id
          ? 'bg-blue-50 text-blue-700'
          : 'text-gray-700 hover:bg-gray-100'
      }`}
    >
      {icon}
      <span className="ml-3">{label}</span>
    </button>
  );
  
  return (
    <div className="bg-gray-50 min-h-screen p-6">
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Account Settings</h1>
        <p className="text-gray-600">Manage your profile, security, and preferences</p>
      </header>

      {/* Alert Message */}
      {alert.show && (
        <div className={`mb-6 p-4 rounded-lg flex items-start ${
          alert.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {alert.type === 'success' ? (
            <CheckCircle className="h-5 w-5 mr-3 mt-0.5" />
          ) : (
            <AlertCircle className="h-5 w-5 mr-3 mt-0.5" />
          )}
          <span>{alert.message}</span>
          <button 
            onClick={() => setAlert({ show: false, type: '', message: '' })}
            className="ml-auto"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Navigation */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex flex-col space-y-1">
            <NavItem
              icon={<User size={20} />}
              label="Profile"
              id="profile"
            />
            <NavItem
              icon={<Lock size={20} />}
              label="Password"
              id="password"
            />
            <NavItem
              icon={<Bell size={20} />}
              label="Notifications"
              id="notifications"
            />
            <NavItem
              icon={<CreditCard size={20} />}
              label="Billing"
              id="subscription"
            />
            <NavItem
              icon={<Shield size={20} />}
              label="Privacy"
              id="privacy"
            />
          </div>
          
          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={signOut}
              className="flex items-center w-full p-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut size={20} />
              <span className="ml-3">Sign Out</span>
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="bg-white rounded-lg shadow-sm p-6 lg:col-span-3">
          {/* Profile Section */}
          {activeSection === 'profile' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-6">Profile Information</h2>
              
              {/* Avatar */}
              {/* <div className="flex items-center mb-8">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-gray-200 overflow-hidden">
                    {profile.avatar_url ? (
                      <img 
                        src={profile.avatar_url} 
                        alt="Profile avatar" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-blue-100 text-blue-500">
                        <User size={40} />
                      </div>
                    )}
                  </div>
                  <label className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full cursor-pointer hover:bg-blue-700 transition-colors">
                    <Camera size={16} />
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={uploadAvatar}
                    />
                  </label>
                </div>
                <div className="ml-5">
                  <h3 className="font-medium text-gray-800">Profile Picture</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    JPG, GIF or PNG. Max size 1MB
                  </p>
                </div>
              </div> */}
              
              <form onSubmit={updateProfile}>
                <div className="grid grid-cols-1 gap-6">
                  {/* Full Name */}
                  <div>
                    <label 
                      htmlFor="full_name" 
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="full_name"
                      name="full_name"
                      value={profile.full_name ?? ""}
                      onChange={handleProfileChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Your full name"
                      required
                    />
                  </div>
                  
                  {/* Email Address */}
                  <div>
                    <label 
                      htmlFor="email" 
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={profile.email ?? ""}
                      onChange={handleProfileChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      placeholder="Your email address"
                      disabled
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      To change your email, please contact support
                    </p>
                  </div>
                  
                  {/* Save Button */}
                  <div className="mt-4">
                    <button
                      type="submit"
                      className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      disabled={loading}
                    >
                      {loading ? (
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <Save size={18} className="mr-2" />
                      )}
                      Save Changes
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* Password Section */}
          {activeSection === 'password' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-6">Change Password</h2>
              
              <form onSubmit={changePassword}>
                <div className="grid grid-cols-1 gap-6">
                  {/* Current Password */}
                  <div>
                    <label 
                      htmlFor="currentPassword" 
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Current Password
                    </label>
                    <input
                      type="password"
                      id="currentPassword"
                      name="currentPassword"
                      value={passwords.currentPassword}
                      onChange={handlePasswordChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter your current password"
                      required
                    />
                  </div>
                  
                  {/* New Password */}
                  <div>
                    <label 
                      htmlFor="newPassword" 
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      New Password
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      value={passwords.newPassword}
                      onChange={handlePasswordChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter new password"
                      required
                    />
                  </div>
                  
                  {/* Confirm New Password */}
                  <div>
                    <label 
                      htmlFor="confirmPassword" 
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={passwords.confirmPassword}
                      onChange={handlePasswordChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Confirm new password"
                      required
                    />
                  </div>
                  
                  {/* Password Requirements */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-2">Password Requirements:</h4>
                    <ul className="space-y-1">
                      <li className="text-sm text-gray-600 flex items-center">
                        <div className={`w-4 h-4 rounded-full mr-2 flex items-center justify-center ${
                          passwords.newPassword.length >= 6 ? 'bg-green-100' : 'bg-gray-200'
                        }`}>
                          {passwords.newPassword.length >= 6 && (
                            <CheckCircle size={12} className="text-green-600" />
                          )}
                        </div>
                        At least 6 characters
                      </li>
                      <li className="text-sm text-gray-600 flex items-center">
                        <div className={`w-4 h-4 rounded-full mr-2 flex items-center justify-center ${
                          /[A-Z]/.test(passwords.newPassword) ? 'bg-green-100' : 'bg-gray-200'
                        }`}>
                          {/[A-Z]/.test(passwords.newPassword) && (
                            <CheckCircle size={12} className="text-green-600" />
                          )}
                        </div>
                        At least one uppercase letter
                      </li>
                      <li className="text-sm text-gray-600 flex items-center">
                        <div className={`w-4 h-4 rounded-full mr-2 flex items-center justify-center ${
                          /[0-9]/.test(passwords.newPassword) ? 'bg-green-100' : 'bg-gray-200'
                        }`}>
                          {/[0-9]/.test(passwords.newPassword) && (
                            <CheckCircle size={12} className="text-green-600" />
                          )}
                        </div>
                        At least one number
                      </li>
                    </ul>
                  </div>
                  
                  {/* Save Button */}
                  <div className="mt-4">
                    <button
                      type="submit"
                      className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      disabled={loading}
                    >
                      {loading ? (
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <Lock size={18} className="mr-2" />
                      )}
                      Update Password
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* Notifications Section */}
          {activeSection === 'notifications' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-6">Notification Preferences</h2>
              
              <div className="space-y-6">
                <div className="border-b border-gray-200 pb-6">
                  <h3 className="font-medium text-gray-800 mb-4">Email Notifications</h3>
                  
                  {/* Email Notifications Toggles */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-700">Test Reminders</p>
                        <p className="text-sm text-gray-500">Receive notifications about new tests</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          className="sr-only peer"
                          checked={notifications.newTests}
                          onChange={() => handleNotificationChange('newTests')}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-700">Reports & Analytics</p>
                        <p className="text-sm text-gray-500">Get notified when your reports and analytics are available</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          className="sr-only peer"
                          checked={notifications.reportsAnalytics}
                          onChange={() => handleNotificationChange('reportsAnalytics')}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    
                    {/* <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-700">Product Updates</p>
                        <p className="text-sm text-gray-500">Stay informed about new features and improvements</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          className="sr-only peer"
                          checked={notifications.emailUpdates}
                          onChange={() => handleNotificationChange('emailUpdates')}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div> */}
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-700">Marketing Emails</p>
                        <p className="text-sm text-gray-500">Receive promotional offers and news</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          className="sr-only peer"
                          checked={notifications.marketingEmails}
                          onChange={() => handleNotificationChange('marketingEmails')}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                </div>
                
                {/* Communication Preferences */}
                <div>
                  <h3 className="font-medium text-gray-800 mb-4">Communication Preferences</h3>
                  
                  <p className="text-sm text-gray-600 mb-4">
                    How would you prefer to receive notifications?
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        id="comm-email"
                        name="communication"
                        type="radio"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        checked={profile?.communication_preference === 0}
                        onChange={(e) => e.target.checked && setProfile({
                          ...profile,
                          communication_preference: 0,
                        })}
                      />
                      <label htmlFor="comm-email" className="ml-3 text-gray-700">
                        Email
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="comm-app"
                        name="communication"
                        type="radio"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        checked={profile?.communication_preference === 1}
                        onChange={(e) => e.target.checked && setProfile({
                          ...profile,
                          communication_preference: 1,
                        })}
                      />
                      <label htmlFor="comm-app" className="ml-3 text-gray-700">
                        In-app notifications only
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="comm-none"
                        name="communication"
                        type="radio"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        checked={profile?.communication_preference === 2}
                        onChange={(e) => e.target.checked && setProfile({
                          ...profile,
                          communication_preference: 2,
                        })}
                      />
                      <label htmlFor="comm-none" className="ml-3 text-gray-700">
                        None
                      </label>
                    </div>
                  </div>
                </div>
                
                {/* Save Button */}
                <div className="mt-6">
                  <button
                    onClick={saveNotificationSettings}
                    className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    disabled={loading}
                  >
                    {loading ? (
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <Save size={18} className="mr-2" />
                    )}
                    Save Preferences
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Subscription Section */}
          {activeSection === 'subscription' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-6">Billing Management</h2>              
              {/* Current Plan */}
              {/* <div className="bg-blue-50 border border-blue-100 rounded-lg p-6 mb-8">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-blue-700">Current Plan</p>
                    <h3 className="text-2xl font-bold text-gray-800 mt-1">{subscription.plan}</h3>
                    <p className="text-sm text-gray-600 mt-2">
                      Next billing date: <span className="font-semibold">{subscription.nextBillingDate}</span>
                    </p>
                  </div>
                  <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                    {subscription.status}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                  <div>
                    <p className="text-sm text-gray-500">Monthly Price</p>
                    <p className="text-lg font-semibold text-gray-800">$29.99</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Practice Tests</p>
                    <p className="text-lg font-semibold text-gray-800">Unlimited</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Analytics Access</p>
                    <p className="text-lg font-semibold text-gray-800">Full Access</p>
                  </div>
                </div>
                
                <div className="mt-6 flex gap-3">
                  <button className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors">
                    Upgrade Plan
                  </button>
                  <button className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors">
                    Cancel Subscription
                  </button>
                </div>
              </div>
               */}
              {/* Payment Method */}
              <Button component={Link} to={import.meta.env.STRIPE_BILLING_LINK} variant="outline">
                Manage Billing &amp; Subscription
              </Button>
              {/* <div className="border-b border-gray-200 pb-6 mb-6">
                <h3 className="font-medium text-gray-800 mb-4">Payment Method</h3>
                
                <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="bg-blue-100 p-2 rounded">
                      <CreditCard size={24} className="text-blue-600" />
                    </div>
                    <div className="ml-4">
                      <p className="font-medium text-gray-800">Visa ending in {subscription.cardLast4}</p>
                      <p className="text-sm text-gray-500">Expires 09/2026</p>
                    </div>
                  </div>
                  <button className="text-blue-600 hover:text-blue-800">
                    Edit
                  </button>
                </div>
                
                <button className="mt-4 text-blue-600 font-medium hover:text-blue-800 inline-flex items-center">
                  <span className="mr-1">+</span> Add new payment method
                </button>
              </div> */}
              
              {/* Billing History */}
              <div>
                <OrderHistory />

                {/* <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Invoice
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          March 2, 2025
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          $29.99
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Paid
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 hover:text-blue-800">
                          <a href="#" className="underline">Download</a>
                        </td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          February 2, 2025
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          $29.99
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Paid
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 hover:text-blue-800">
                          <a href="#" className="underline">Download</a>
                        </td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          January 2, 2025
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          $29.99
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Paid
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 hover:text-blue-800">
                          <a href="#" className="underline">Download</a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div> */}
              </div>
            </div>
          )}

          {/* Privacy Section */}
          {activeSection === 'privacy' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-6">Privacy Settings</h2>
              
              <div className="space-y-6">
                {/* Data Sharing */}
                <div className="border-b border-gray-200 pb-6">
                  <h3 className="font-medium text-gray-800 mb-4">Data Sharing</h3>
                  
                  <div className="space-y-4">
                    {/* <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-700">Share my progress with my instructor</p>
                        <p className="text-sm text-gray-500">Allow your instructor to view your test results and progress</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" defaultChecked />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div> */}
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-700">Anonymous data for research</p>
                        <p className="text-sm text-gray-500">Allow us to use your anonymized data to improve our platform</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox" className="sr-only peer"
                          checked={profile?.settings_anonymous_research === true}
                          onChange={(e) => setProfile({
                            ...profile,
                            settings_anonymous_research: e.target.checked,
                          })}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-700">Personalized recommendations</p>
                        <p className="text-sm text-gray-500">Allow us to use your data to provide personalized study recommendations</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer"
                          checked={profile?.settings_personalized_recommendations === true}
                          onChange={(e) => setProfile({
                            ...profile,
                            settings_personalized_recommendations: e.target.checked,
                          })}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                </div>
                
                {/* Data Management */}
                <div className="hidden">
                  <h3 className="font-medium text-gray-800 mb-4">Data Management</h3>
                  
                  <div className="space-y-4">
                    {/* <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <p className="font-medium text-gray-700">Download your data</p>
                      <p className="text-sm text-gray-500 mt-1">Get a copy of all your personal data</p>
                      <button className="mt-3 px-4 py-2 bg-blue-100 text-blue-700 font-medium rounded-lg hover:bg-blue-200 transition-colors">
                        Request data export
                      </button>
                    </div> */}
                    
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <p className="font-medium text-gray-700">Delete your account</p>
                      <p className="text-sm text-gray-500 mt-1">Permanently delete your account and all associated data</p>
                      <button className="mt-3 px-4 py-2 bg-red-100 text-red-700 font-medium rounded-lg hover:bg-red-200 transition-colors">
                        Delete account
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Save Button */}
                <div className="mt-6">
                  <button
                    className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    onClick={saveNotificationSettings}
                    disabled={loading}
                  >
                    <Save size={18} className="mr-2" />
                    Save Privacy Settings
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountSettingsPage;
